import type { WsEvent } from '../types'

type Handler = (event: WsEvent) => void

class WebSocketClient {
  private ws: WebSocket | null = null
  private handlers: Handler[] = []
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private reconnectDelay = 1000
  private intentionalClose = false
  // mark.read commands queued while WS is connecting — flushed on open
  private markReadQueue: Array<{ chat_id: string; message_id: string }> = []

  connect(token: string) {
    // Всегда чисто закрываем старое соединение перед новым
    this.disconnect()
    this.intentionalClose = false
    this.reconnectDelay = 1000
    this._connect(token)
  }

  private _connect(token: string) {
    // Не создаём новое соединение, если уже есть активное
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      return
    }
    const base = import.meta.env.VITE_API_URL ?? ''
    let wsBase: string
    if (base.startsWith('http')) {
      wsBase = base.replace(/^http/, 'ws')
    } else {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      wsBase = `${proto}//${window.location.host}${base}`
    }
    const url = `${wsBase}/v1/messaging/ws?token=${encodeURIComponent(token)}`

    this.ws = new WebSocket(url)

    this.ws.onopen = () => {
      this.reconnectDelay = 1000
      this._startPing()
      // Flush any mark.read commands queued while connecting
      const queue = this.markReadQueue.splice(0)
      for (const cmd of queue) {
        this.ws!.send(JSON.stringify({ type: 'mark.read', payload: cmd }))
      }
    }

    this.ws.onmessage = (event) => {
      // Сервер может слать несколько событий разделённых \n
      const lines = (event.data as string).split('\n').filter(Boolean)
      for (const line of lines) {
        try {
          const parsed: WsEvent = JSON.parse(line)
          this.handlers.forEach((h) => h(parsed))
        } catch {
          // ignore malformed frames
        }
      }
    }

    this.ws.onclose = () => {
      this._stopPing()
      if (!this.intentionalClose) {
        this.reconnectTimer = setTimeout(() => {
          this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30_000)
          // Берём самый свежий токен из localStorage — он мог обновиться через refresh
          const latestToken = localStorage.getItem('access_token') ?? token
          this._connect(latestToken)
        }, this.reconnectDelay)
      }
    }

    this.ws.onerror = () => {
      this.ws?.close()
    }
  }

  send(type: string, payload: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, payload }))
    }
  }

  sendTypingStart(chatId: string) {
    this.send('typing.start', { chat_id: chatId })
  }

  sendTypingStop(chatId: string) {
    this.send('typing.stop', { chat_id: chatId })
  }

  sendMarkRead(chatId: string, messageId: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'mark.read', payload: { chat_id: chatId, message_id: messageId } }))
    } else {
      // Queue and flush on connect — preserves only the latest per chat
      this.markReadQueue = this.markReadQueue.filter((c) => c.chat_id !== chatId)
      this.markReadQueue.push({ chat_id: chatId, message_id: messageId })
    }
  }

  on(handler: Handler) {
    this.handlers.push(handler)
    return () => {
      this.handlers = this.handlers.filter((h) => h !== handler)
    }
  }

  disconnect() {
    this.intentionalClose = true
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this._stopPing()
    if (this.ws) {
      // Зануляем хэндлеры до close() чтобы не сработал reconnect
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.close()
      this.ws = null
    }
  }

  private _startPing() {
    this._stopPing()
    this.pingTimer = setInterval(() => {
      this.send('ping', {})
    }, 30_000)
  }

  private _stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer)
      this.pingTimer = null
    }
  }
}

export const wsClient = new WebSocketClient()
